import type { User as SupabaseUser } from "@supabase/supabase-js";
import {
  captureGoogleAnalyticsLogin,
  setGoogleAnalyticsUser,
} from "@/lib/analytics/googleAnalytics";
import { withPosthog } from "@/lib/analytics/posthog";
import { withBrowserSentry } from "@/lib/sentry/browserSentry";

function isLocalhost(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1" ||
    window.location.hostname === "0.0.0.0"
  );
}

const LOGIN_CAPTURED_KEY = "analytics-login-captured";

/**
 * Whether `user_logged_in` already went out for this sign-in. Supabase
 * repeats SIGNED_IN in every open tab and on some page loads, while
 * `last_sign_in_at` only changes on a real sign-in.
 */
function claimLoginCapture(user: SupabaseUser): boolean {
  const signIn = `${user.id}:${user.last_sign_in_at ?? ""}`;
  try {
    if (window.localStorage.getItem(LOGIN_CAPTURED_KEY) === signIn) {
      return false;
    }
    window.localStorage.setItem(LOGIN_CAPTURED_KEY, signIn);
  } catch {
    // Storage blocked: capture rather than lose the sign-in.
  }
  return true;
}

/**
 * Identifies a user in both PostHog and Sentry analytics platforms.
 * `captureLogin` records `user_logged_in`; pass it only for a real sign-in,
 * not for a restored session on page load.
 */
export const identifyUser = (
  user: SupabaseUser,
  options: { captureLogin: boolean },
): void => {
  if (isLocalhost()) {
    return;
  }

  setGoogleAnalyticsUser(user.id);
  withPosthog("identifyUser", (posthog) => {
    posthog.identify(user.id, {
      email: user.email,
      created_at: user.created_at,
      last_sign_in_at: user.last_sign_in_at,
      email_verified: Boolean(user.email_confirmed_at),
      is_anonymous: Boolean(user.is_anonymous),
    });
    if (options.captureLogin && claimLoginCapture(user)) {
      posthog.capture("user_logged_in", {
        user_id: user.id,
        email: user.email,
      });
      captureGoogleAnalyticsLogin();
    }
  });

  withBrowserSentry((sdk) => {
    sdk.setUser({
      id: user.id,
      email: user.email,
    });
    sdk.setContext("user", {
      created_at: user.created_at,
      last_sign_in_at: user.last_sign_in_at,
      email_verified: Boolean(user.email_confirmed_at),
    });
  });
};

/**
 * Records the sign-out on the signed-in person, then clears PostHog and
 * Sentry identity. Call only for a real sign-out: resetting PostHog gives
 * the browser a new anonymous id.
 */
export const clearUserIdentification = (): void => {
  if (isLocalhost()) {
    return;
  }

  withPosthog("clearUserIdentification", (posthog) => {
    posthog.capture("user_logged_out");
    posthog.reset();
  });

  withBrowserSentry((sdk) => {
    sdk.setUser(null);
  });
};
